/**
 * Shared display helpers for ontology entities — node/relationship names,
 * logical ids, and decoded property values. One home so the commit-detail
 * view, the merge compare preview, and the conflict resolver all render
 * entities identically (instead of re-deriving the `start —TYPE→ end` arrow
 * form or the value rendering inline in each).
 */
import {
  decodedValueEqual,
  decodeTaggedProps,
  type DecodedValue,
  type TaggedNode,
  type TaggedRel
} from '@axilon-apps/domains-ontology-ui-state'

// Relationship logical ids are the endpoints + type joined by the ASCII unit
// separator: `start␟TYPE␟end`.
const UNIT_SEPARATOR = String.fromCharCode(31)

/**
 * The readable tail of a URN: the last path segment with any `kind:` prefix
 * stripped, then percent-decoded and reduced to its final path component —
 * "urn:…/folder:Gateway%2F…%2FStorageMetrics" → "StorageMetrics".
 */
export function urnDisplayName(urn: string): string {
  const segment = urn.split('/').pop() ?? urn
  const colon = segment.lastIndexOf(':')
  let tail = colon >= 0 ? segment.slice(colon + 1) : segment
  try {
    tail = decodeURIComponent(tail)
  } catch {
    // Not valid percent-encoding — keep the raw tail.
  }
  const last = tail.split('/').pop() ?? tail
  return last.length > 0 ? last : urn
}

/** A node's display name: its `name` property if present, else the URN tail. */
export function entityDisplayName(entity: TaggedNode): string {
  try {
    const props = decodeTaggedProps(entity.properties ?? {})
    const name = props.name
    if (typeof name === 'string' && name.length > 0) return name
  } catch {
    // Fall through to the URN tail.
  }
  return urnDisplayName(entity.id)
}

/** The canonical relationship arrow form: `start —TYPE→ end`. */
export function formatRelArrow(start: string, type: string, end: string): string {
  return `${start} —${type}→ ${end}`
}

/**
 * A relationship's concise human label — endpoint display names with a plain
 * arrow (`pump-1 → valve-7`). The type is carried by the group header / the
 * full id, so it is left off here.
 */
export function relLabel(rel: Pick<TaggedRel, 'startId' | 'endId'>): string {
  return `${urnDisplayName(rel.startId)} → ${urnDisplayName(rel.endId)}`
}

/** A relationship's full logical id in arrow form: full endpoint URNs + type. */
export function relFullId(rel: Pick<TaggedRel, 'startId' | 'type' | 'endId'>): string {
  return formatRelArrow(rel.startId, rel.type, rel.endId)
}

/**
 * Render a logical id string for display. A composite relationship id
 * (`start␟TYPE␟end`) becomes the `start —TYPE→ end` arrow form so it never
 * shows as a control-character glyph; a plain node URN has no separator and is
 * returned unchanged.
 */
export function formatLogicalId(id: string): string {
  const parts = id.split(UNIT_SEPARATOR)
  return parts.length === 3 ? formatRelArrow(parts[0], parts[1], parts[2]) : id
}

/** Whether two label sets differ (order-insensitive; absent = empty). */
export function labelsDiverge(a?: string[], b?: string[]): boolean {
  const sa = [...(a ?? [])].sort()
  const sb = [...(b ?? [])].sort()
  return sa.length !== sb.length || sa.some((label, i) => label !== sb[i])
}

/** Renders one decoded value for display; ints render their exact string. */
export function renderValue(value: DecodedValue): string {
  if (value === null) return 'null'
  if (typeof value === 'string') return value
  if (typeof value === 'boolean') return String(value)
  if (Array.isArray(value)) return `[${value.map(renderValue).join(', ')}]`
  switch (value.kind) {
    case 'int':
    case 'float':
      return value.text
    case 'bytes':
      return `bytes(${value.base64})`
    case 'date':
    case 'localtime':
    case 'offsettime':
    case 'localdatetime':
      return value.text
    case 'zoneddatetime':
      return value.tz ? `${value.text} (${value.tz})` : value.text
    case 'duration':
      return `${value.months}mo ${value.days}d ${value.seconds}s ${value.nanos}ns`
    case 'point':
      return value.z !== undefined
        ? `(${value.x}, ${value.y}, ${value.z}) SRID ${value.srid}`
        : `(${value.x}, ${value.y}) SRID ${value.srid}`
  }
}

/**
 * A decoded value's kind, spelled for display — the disambiguator when two
 * different values render to the same text.
 */
export function kindLabel(value: DecodedValue): string {
  if (value === null) return 'null'
  if (typeof value === 'string') return 'text'
  if (typeof value === 'boolean') return 'boolean'
  if (Array.isArray(value)) {
    const kinds = [...new Set(value.map(kindLabel))]
    return kinds.length === 1 ? `list of ${kinds[0]}` : 'list'
  }
  return value.kind
}

/**
 * Renders a decoded value with its kind spelled out — `5 (int)` vs
 * `5 (float)` — for the places where two structurally different values would
 * otherwise display identically.
 */
export function renderValueDisambiguated(value: DecodedValue): string {
  return `${renderValue(value)} (${kindLabel(value)})`
}

/**
 * Property keys whose before/after values differ structurally — never by
 * rendered text, which masks type-only differences (int 5 vs float 5.0).
 */
export function differingKeys(
  before: Record<string, DecodedValue>,
  after: Record<string, DecodedValue>
): string[] {
  const keys: string[] = []
  for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
    const inBefore = key in before
    const inAfter = key in after
    if (inBefore !== inAfter || (inBefore && !decodedValueEqual(before[key], after[key]))) {
      keys.push(key)
    }
  }
  return keys
}
